import { Notification } from "orcs-design-system";
import PropTypes from "prop-types";
import React from "react";
import { create } from "zustand";

import { AIDashboardContext, makeChannelName } from "./AIDashboardContext";

const channelStores = new Map();
let lastRateLimitNotification = 0;
const RATE_LIMIT_NOTIFICATION_COOLDOWN = 5000; // 5 seconds between notifications

/**
 * Creates a rate-limited publish function using a token bucket algorithm
 * @param {Function} set - Zustand set function
 * @param {Function} onRateLimitExceeded - Callback when rate limit is hit
 * @param {number} maxMessages - Maximum messages per window (default: 50)
 * @param {number} windowMs - Time window in milliseconds (default: 1000ms)
 * @returns {Function} Rate-limited publish function
 */
function createRateLimitedPublish(
  set,
  onRateLimitExceeded,
  maxMessages = 50,
  windowMs = 1000
) {
  let tokens = maxMessages;
  let lastRefill = Date.now();

  return (data) => {
    const now = Date.now();
    const elapsed = now - lastRefill;

    // Refill tokens based on time elapsed
    if (elapsed > 0) {
      const tokensToAdd = (elapsed / windowMs) * maxMessages;
      tokens = Math.min(maxMessages, tokens + tokensToAdd);
      lastRefill = now;
    }

    // Only publish if we have tokens
    if (tokens >= 1) {
      tokens -= 1;
      set({ value: data });
    } else {
      // Rate limit exceeded
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error(
          `Dashboard message rate limit exceeded (${maxMessages} messages/${windowMs}ms). Message dropped.`
        );
      }
      onRateLimitExceeded();
    }
  };
}

function getChannelStore(channelName, onRateLimitExceeded) {
  if (!channelStores.has(channelName)) {
    channelStores.set(
      channelName,
      create((set) => ({
        value: null,
        publish: createRateLimitedPublish(set, onRateLimitExceeded, 50, 1000),
      }))
    );
  }
  return channelStores.get(channelName);
}

export const AIDashboardProvider = ({ children, chatId, dashboardId }) => {
  const [showRateLimitError, setShowRateLimitError] = React.useState(false);

  const handleRateLimitExceeded = React.useCallback(() => {
    const now = Date.now();
    // Only show notification if enough time has passed since last notification
    if (now - lastRateLimitNotification > RATE_LIMIT_NOTIFICATION_COOLDOWN) {
      lastRateLimitNotification = now;
      setShowRateLimitError(true);
    }
  }, []);

  const storeRetriever = React.useCallback(
    (channel) => {
      return getChannelStore(
        makeChannelName(chatId, dashboardId, channel),
        handleRateLimitExceeded
      );
    },
    [chatId, dashboardId, handleRateLimitExceeded]
  );

  return (
    <AIDashboardContext.Provider
      value={{ storeRetriever, chatId, dashboardId }}
    >
      {children}
      {showRateLimitError && (
        <Notification
          colour="warning"
          icon={["fas", "exclamation-triangle"]}
          closable
          floating
          centered
          onDismiss={() => setShowRateLimitError(false)}
          bottom="20px"
        >
          Dashboard is sending too many updates. Some messages have been dropped
          to prevent browser performance issues.
        </Notification>
      )}
    </AIDashboardContext.Provider>
  );
};

AIDashboardProvider.propTypes = {
  children: PropTypes.node.isRequired,
  chatId: PropTypes.string.isRequired,
  dashboardId: PropTypes.string.isRequired,
};

export default AIDashboardProvider;
